// Filters data based on search term
export const searchData = <T>(
    data: T[],
    searchTerm: string,
    searchableFields: Partial<Record<keyof T, string>>
): T[] => {
    if (!searchTerm.trim()) {
        return data;
    }

    const lowercaseSearchTerm = searchTerm.toLowerCase().trim();

    return data.filter((item) => {
        // Check if search term matches any of the searchable fields
        return Object.keys(searchableFields).some((field) => {
            const fieldValue = item[field as keyof T];
            return (
                fieldValue && fieldValue.toString().toLowerCase().includes(lowercaseSearchTerm)
            );
        });
    });
};

const calculateSearchRelevance = <T>(
    item: T,
    searchTerm: string,
    searchableFields: Partial<Record<keyof T, string>>
): number => {
    const lowercaseSearchTerm = searchTerm.toLowerCase();
    let score = 0;

    // Weight different fields differently in case both have the searched term
    const fieldWeights: Record<keyof T, number> =
        Object.keys(searchableFields).reduce((weights, field, index) => {
            weights[field as keyof T] = index + 1;
            return weights;
        }, {} as Record<keyof T, number>);

    Object.entries(fieldWeights).forEach(([field, weight]) => {
        const fieldValue = item[field as keyof T];
        if (fieldValue && typeof weight === 'number') {
            const lowercaseFieldValue = fieldValue.toString().toLowerCase();

            // Exact match gets highest score
            if (lowercaseFieldValue === lowercaseSearchTerm) score += weight * 10;
            // Starts with search term gets high score
            else if (lowercaseFieldValue.startsWith(lowercaseSearchTerm))
                score += weight * 7;
            // Contains search term gets moderate score
            else if (lowercaseFieldValue.includes(lowercaseSearchTerm))
                score += weight * 3;
        }
    });

    return score;
};

// Sort search results by relevance
export const sortBySearchRelevance = <T>(
    items: T[],
    searchTerm: string,
    searchableFields: Partial<Record<keyof T, string>>
): T[] => {
    if (!searchTerm.trim()) return items;

    return items
        .map((item) => ({
            item,
            relevance: calculateSearchRelevance(item, searchTerm, searchableFields),
        }))
        .sort((a, b) => b.relevance - a.relevance)
        .map(({ item }) => item);
};
